/**
 * Confine di errore dell'applicazione.
 *
 * Un'eccezione non gestita in un componente non deve lasciare l'operatore
 * davanti a una schermata bianca durante il servizio: qui viene intercettata,
 * registrata e trasformata in un messaggio con una via d'uscita.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertOctagon } from 'lucide-react';
import { branding } from '@/config/branding';
import { logError } from '@/lib/observability';
import { Button } from './ui/primitives';

interface Props {
  children: ReactNode;
  /** Etichetta della sezione, per distinguere gli errori nei log. */
  scope?: string;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    logError(error, {
      scope: this.props.scope ?? 'app',
      componentStack: info.componentStack ?? undefined,
    });
  }

  private readonly reset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="flex min-h-64 flex-col items-center justify-center gap-4 p-8 text-center">
        <AlertOctagon className="size-10 text-destructive" aria-hidden="true" />
        <div className="space-y-1">
          <h2 className="text-lg font-semibold">Qualcosa non ha funzionato</h2>
          <p className="max-w-md text-sm text-muted-foreground">
            La sezione non è stata caricata correttamente. Puoi riprovare senza perdere il lavoro svolto
            nelle altre schermate.
          </p>
          <p className="text-xs text-muted-foreground">
            Se il problema persiste, scrivi a{' '}
            <a className="underline" href={`mailto:${branding.company.supportEmail}`}>
              {branding.company.supportEmail}
            </a>
            .
          </p>
        </div>
        <details className="max-w-xl text-left">
          <summary className="cursor-pointer text-xs text-muted-foreground">Dettagli tecnici</summary>
          <pre className="mt-2 overflow-x-auto rounded-md bg-muted p-3 text-xs">{error.message}</pre>
        </details>
        <div className="flex gap-2">
          <Button variant="outline" onClick={this.reset}>
            Riprova
          </Button>
          <Button onClick={() => window.location.reload()}>Ricarica l’applicazione</Button>
        </div>
      </div>
    );
  }
}
